export interface ExpenseMonthlyPoint {
    period: string;
    type: string;
    amount: number;
}

export interface ExpenseTypeTotal {
    type: string;
    amount: number;
}

export interface ExpensesResponse {
    monthly: ExpenseMonthlyPoint[];
    byType: ExpenseTypeTotal[];
}

export interface GoodsSalesMonthlyPoint {
    period: string;
    value: number;
    sales: number;
}

export interface GoodsSalesResponse {
    monthly: GoodsSalesMonthlyPoint[];
}

export interface ExpensePiePoint {
    name: string;
    y: number;
}

export interface MonthlyExpenseSeries {
    periods: string[];
    series: Array<{ type: string; name: string; data: number[] }>;
}

/** Expenses are stored in Lei; EUR display divides by the page exchange rate. */
export function expenseDisplayFactor(currency: 'EUR' | 'LEI', exchangeRate: number): number {
    return currency === 'EUR' && exchangeRate > 0 ? 1 / exchangeRate : 1;
}

/** Whole currency units, per the repo-wide money display rule. */
function roundMoney(value: number): number {
    return Math.round(value);
}

/** Build pie slices from per-type totals, dropping empty types. */
export function buildExpensePieData(
    byType: readonly ExpenseTypeTotal[],
    factor: number,
    labelFor: (type: string) => string,
): ExpensePiePoint[] {
    return byType
        .filter(entry => entry.amount > 0)
        .map(entry => ({ name: labelFor(entry.type), y: roundMoney(entry.amount * factor) }));
}

/** Pivot monthly points into one aligned column series per expense type. */
export function buildMonthlyExpenseSeries(
    monthly: readonly ExpenseMonthlyPoint[],
    factor: number,
    labelFor: (type: string) => string,
): MonthlyExpenseSeries {
    const periods = [...new Set(monthly.map(point => point.period))].sort((a, b) =>
        a.localeCompare(b),
    );
    const types = [...new Set(monthly.map(point => point.type))];
    const series = types.map(type => ({
        type,
        name: labelFor(type),
        data: periods.map(period => {
            const match = monthly.find(point => point.period === period && point.type === type);
            return roundMoney((match?.amount ?? 0) * factor);
        }),
    }));
    return { periods, series };
}
